import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}
if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

let currentColor = null;

function filterGallery(color) {
  const gallery = document.querySelector('media-gallery');
  if (!gallery) return;

  const isDesktop = window.matchMedia('(min-width: 990px)').matches;

  gallery.querySelectorAll('[data-media-id]').forEach(function (item) {
    const img = item.querySelector('img');
    if (!img) return;
    const alt = (img.getAttribute('alt') || '').toLowerCase().trim();
    const slide = item.closest('slideshow-slide') || item;
    if (alt === color) {
      slide.removeAttribute('hidden');
      slide.style.display = '';
      slide.style.width = '';
      slide.style.minWidth = '';
      slide.style.flex = '';
    } else {
      if (isDesktop) {
        slide.setAttribute('hidden', '');
      } else {
        slide.removeAttribute('hidden');
        slide.style.display = 'none';
        slide.style.width = '0';
        slide.style.minWidth = '0';
        slide.style.flex = '0';
      }
    }
  });

  const slideshow = document.querySelector('media-gallery slideshow-component');
  if (!slideshow) return;

  if (isDesktop) {
    slideshow.disconnectedCallback?.();
    slideshow.connectedCallback?.();
  } else {
    const scroller = slideshow.querySelector('slideshow-slides');
    if (scroller) scroller.scrollLeft = 0;
  }
}

document.addEventListener('variant:update', function (event) {
  const variant = event.detail?.resource;
  if (!variant) return;
  const color = (variant.option1 || '').toLowerCase().trim();
  if (!color) return;
  currentColor = color;
  setTimeout(() => filterGallery(currentColor), 400);
});
// Run on page load with default selected variant
document.addEventListener('DOMContentLoaded', function () {
  setTimeout(function () {
    const checkedInput = document.querySelector('variant-picker input:checked, variant-picker select option[selected]');
    if (!checkedInput) return;
    const color = (checkedInput.value || checkedInput.textContent || '').toLowerCase().trim();
    if (color) {
      currentColor = color;
      filterGallery(currentColor);
    }
  }, 500);
});
